/**
 * Ask the browser to wake the service worker and drain the queue, where it can.
 *
 * Background Sync exists on Chromium (Android and desktop) and not on iOS Safari or Firefox.
 * Where it is missing this quietly does nothing, and that is the design, not a gap: the
 * foreground drain (`foreground.ts`) is the guarantee on every platform, and this is what makes
 * balls go out *sooner* on the ones that support it — including when the app has been closed.
 *
 * Registered after every ball, not once, because a registration is consumed when it fires and
 * the next ball needs a new one. The browser coalesces repeats of one tag.
 */
import { DRAIN_TAG } from "../sw/messages.ts";

/** The slice of `ServiceWorkerRegistration` this needs. An interface so tests need no browser. */
export interface RegistrationLike {
  readonly sync?: { register(tag: string): Promise<void> };
}

/** `getRegistration()`, not `ready`: `ready` never settles when no worker is registered. */
const platformRegistration = (): Promise<RegistrationLike | undefined> =>
  typeof navigator !== "undefined" && "serviceWorker" in navigator
    ? // `sync` is not in TypeScript's DOM lib, so the registration is viewed through the one
      // property this reads. Present only where Background Sync exists.
      (navigator.serviceWorker.getRegistration() as Promise<RegistrationLike | undefined>)
    : Promise.resolve(undefined);

/** Whether a background drain was scheduled. False is never an error. */
export async function requestBackgroundDrain(
  registration: () => Promise<RegistrationLike | undefined> = platformRegistration,
): Promise<boolean> {
  try {
    const found = await registration();
    if (found?.sync === undefined) return false;
    await found.sync.register(DRAIN_TAG);
    return true;
  } catch {
    return false;
  }
}
